import { Point2, LineSegment2 } from 'src/domain/geometry/geometric-types';
import {
  KileTypeSKU,
  Diamond,
} from 'src/components/orderContractor/pdf-shapes/protan/wedge-types';
import { calculateWedgePoints } from 'src/components/orderContractor/pdf-shapes/protan/wedge-calculations';
import { findClosestParallelEdgeToSegment } from 'src/components/orderContractor/pdf-shapes/protan/edge-detection';
import { SheetShapeDeepFragment } from 'src/gql/graphql';
import { SheetScaleType } from 'src/gql/graphql';
import {
  handleMiddlePointDrag,
  handleEndPointDrag,
  handleStartPointDrag,
  calculateProductLengths,
  createWedgeLabel,
  calculateRotationFromSegment,
} from 'src/components/viewers/SheetsViewerV2/shapes/utils/wedge-utils';
import { createDiamondAroundSegment } from 'src/components/viewers/SheetsViewerV2/shapes/utils/wedge-utils';
import { ChakraColor } from 'src/utils/color-util';
import { ApiCoordinatesConverter } from 'src/components/viewers/SheetsViewerV2/utils/apiCoordinatesConverter';
import { ActiveSheetData } from 'src/components/viewers/SheetsViewerV2/hooks/useActiveSheet';
import { AggregatedView } from 'src/components/viewers/SheetsViewerV2/hooks/useSheetsAggregatedView';
import { getUnitScaleFromCalibration } from 'src/domain/sheet-calibration';
import { distanceBetweenInPx } from 'src/components/viewers/SheetsViewerV2/utils/snapping';
import {
  findPointLocations,
  PointLocation,
} from 'src/components/viewers/SheetsViewerV2/utils/drawing';
import {
  CROSSHAIR_CURSOR,
  MOVE_CURSOR,
  VertexStyle,
  FirstVertexStyle,
} from 'src/components/viewers/SheetsViewerV2/SheetsViewerV2.config';
import Edit2D = Autodesk.Extensions.Edit2D;
import Viewer3D = Autodesk.Viewing.Viewer3D;
import GuiViewer3D = Autodesk.Viewing.GuiViewer3D;

interface WedgeDrawingState {
  points: Point2[];
  diamonds: Diamond[];
  kileTypes: KileTypeSKU[];
  defaultMiddleWedgeType: KileTypeSKU;
  availableWedgeTypes: { type: KileTypeSKU; length: number }[];
  isDrawingComplete: boolean;
}

interface WedgePreviewResult {
  points: Point2[];
  diamonds: Diamond[];
  kileTypes: KileTypeSKU[];
  mousePosition?: Point2;
}

type PointWithLocations = {
  point: THREE.Vector3;
  locations: PointLocation[];
};

// Screen-space vertex element that maintains consistent pixel size
class ScreenSpaceVertex {
  private element: HTMLDivElement;
  private viewer: Viewer3D | GuiViewer3D;
  private pointGetter: () => THREE.Vector3;
  private style: any;
  private isFirstVertex: boolean;

  constructor(
    viewer: Viewer3D | GuiViewer3D,
    pointGetter: () => THREE.Vector3,
    style: any,
    isFirstVertex = false
  ) {
    this.viewer = viewer;
    this.pointGetter = pointGetter;
    this.style = style;
    this.isFirstVertex = isFirstVertex;

    this.element = document.createElement('div');
    this.element.style.position = 'absolute';
    this.element.style.pointerEvents = 'none';
    this.element.style.zIndex = '1000';
    this.element.style.width = '8px';
    this.element.style.height = '8px';
    this.element.style.borderRadius = '50%';
    this.element.style.border = `${this.style.lineWidth}px solid ${this.style.lineColor}`;
    this.element.style.backgroundColor = this.style.fillColor;

    // Add to viewer container
    const container = this.viewer.container;
    if (container) {
      container.appendChild(this.element);
    }

    this.updatePosition();
  }

  private updatePosition() {
    // Get the current point position
    const point = this.pointGetter();

    // Convert world coordinates to screen coordinates
    const projected = Autodesk.Viewing.MeasureCommon.project(
      point,
      this.viewer
    );

    // Center the vertex on the point (vertex is 8px wide/high, so subtract 4)
    this.element.style.left = `${projected.x - 4}px`;
    this.element.style.top = `${projected.y - 4}px`;
  }

  public update() {
    this.updatePosition();
  }

  public remove() {
    if (this.element.parentNode) {
      this.element.parentNode.removeChild(this.element);
    }
  }
}

// Screen-space label element that maintains consistent pixel size
class ScreenSpaceLabel {
  private element: HTMLDivElement;
  private viewer: Viewer3D | GuiViewer3D;
  private pointGetter: () => THREE.Vector3;
  private labelGetter: () => string;
  private rotationGetter: () => number;

  constructor(
    viewer: Viewer3D | GuiViewer3D,
    pointGetter: () => THREE.Vector3,
    labelGetter: () => string,
    rotationGetter: () => number
  ) {
    this.viewer = viewer;
    this.pointGetter = pointGetter;
    this.labelGetter = labelGetter;
    this.rotationGetter = rotationGetter;

    this.element = document.createElement('div');
    this.element.style.position = 'absolute';
    this.element.style.pointerEvents = 'none';
    this.element.style.zIndex = '1001';
    this.element.style.fontSize = '12px';
    this.element.style.fontWeight = 'bold';
    this.element.style.color = '#2D3748';
    this.element.style.whiteSpace = 'nowrap';
    this.element.style.transformOrigin = 'center';

    // Add to viewer container
    const container = this.viewer.container;
    if (container) {
      container.appendChild(this.element);
    }

    this.updatePosition();
  }

  private updatePosition() {
    // Get the current point position
    const point = this.pointGetter();
    const label = this.labelGetter();
    const rotation = this.rotationGetter();

    // Convert world coordinates to screen coordinates
    const projected = Autodesk.Viewing.MeasureCommon.project(
      point,
      this.viewer
    );

    // Update label text
    this.element.textContent = label;

    // Position the label
    this.element.style.left = `${projected.x}px`;
    this.element.style.top = `${projected.y}px`;

    // Apply rotation
    this.element.style.transform = `rotate(${rotation}deg) translate(-50%, -50%)`;
  }

  public update() {
    this.updatePosition();
  }

  public remove() {
    if (this.element.parentNode) {
      this.element.parentNode.removeChild(this.element);
    }
  }
}

export class WedgeDrawingTool extends Autodesk.Viewing.ToolInterface {
  names = ['WedgeDrawingTool', 'Wedge'];

  private state: WedgeDrawingState;
  private unitScale: number | null;
  private renderedSheetShapes: Record<string, SheetShapeDeepFragment[]> | null;
  private calibration: SheetScaleType | null;

  // Rendering properties
  private previewShapes: Autodesk.Edit2D.Shape[] = [];
  private staticShapes: Autodesk.Edit2D.Shape[] = [];
  private screenSpaceVertices: ScreenSpaceVertex[] = [];
  private screenSpaceLabels: ScreenSpaceLabel[] = [];

  // Drawing state
  private pointsArray: THREE.Vector3[] = [];
  private cursorPoint: THREE.Vector3 | null = null;
  private hoveredPoint: PointWithLocations | null = null;
  private activePoints: PointWithLocations[] = [];
  private activePointerInitPosition: THREE.Vector3 | null = null;
  private activePointerCurrentPosition: THREE.Vector3 | null = null;
  private dragType: 'start' | 'end' | 'middle' | null = null;
  private dragMiddleIndex: number | null = null;

  // Caching for performance
  private prevVisualizationRef: WedgePreviewResult | null = null;
  private edgeDetectionProcessedRef = false;

  private _onResult?: (result: any) => void;
  private _keyDownHandler: ((event: KeyboardEvent) => void) | null = null;

  constructor(
    private readonly viewer: AggregatedView['viewer'],
    private readonly activeSheetData: ActiveSheetData,
    private readonly edit2d: Edit2D,
    onResult?: (result: any) => void,
    calibration?: SheetScaleType | null,
    renderedSheetShapes?: Record<string, SheetShapeDeepFragment[]> | null
  ) {
    super();

    this._onResult = onResult;
    this.calibration = calibration || null;
    this.renderedSheetShapes = renderedSheetShapes || null;

    // Get unit scale from calibration
    this.unitScale = this.calibration
      ? getUnitScaleFromCalibration(this.calibration)
      : null;

    this.state = {
      points: [],
      diamonds: [
        {
          triangle1: true,
          triangle2: true,
          triangle3: true,
          triangle4: true,
        },
      ],
      kileTypes: [KileTypeSKU.RW257379],
      defaultMiddleWedgeType: KileTypeSKU.RW257379,
      availableWedgeTypes: this.getAvailableWedgeTypes(),
      isDrawingComplete: false,
    };

    // Remove default methods to prevent conflicts
    delete (this as any).handleSingleClick;
    delete (this as any).handleMouseMove;
    delete (this as any).handleButtonDown;
    delete (this as any).handleButtonUp;
    delete (this as any).getPriority;
    delete (this as any).activate;
    delete (this as any).deactivate;
  }

  private getAvailableWedgeTypes(): { type: KileTypeSKU; length: number }[] {
    const types: { type: KileTypeSKU; length: number }[] = [];

    // Extract kile types and their lengths from the calculations utility
    Object.values(KileTypeSKU).forEach((key) => {
      if (key !== KileTypeSKU.INVALID_KILE) {
        const length = 2.4; // Default for the sorted list - actual lengths will be calculated on demand
        types.push({ type: key, length });
      }
    });

    // Sort by length (ascending)
    return types.sort((a, b) => a.length - b.length);
  }

  getPriority(): number {
    return 100;
  }

  activate(): void {
    this.viewer.setSelectionMode(Autodesk.Viewing.SelectionMode.MIXED);
    this.viewer.impl.canvas.style.cursor = CROSSHAIR_CURSOR;

    // Set up camera change listener to update screen-space vertices
    this.viewer.addEventListener(
      Autodesk.Viewing.CAMERA_CHANGE_EVENT,
      this._onCameraChange
    );

    // Add keyboard event listener for wedge type changes
    this._keyDownHandler = (event: KeyboardEvent) => {
      // Only handle arrow keys when we're actively drawing wedges
      if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
        // Check if we're in preview mode (after first click, before second)
        if (this.pointsArray.length === 1 && !this.state.isDrawingComplete) {
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();

          // Handle key changes directly in the event handler
          const currentIndex = this.state.availableWedgeTypes.findIndex(
            (item) => item.type === this.state.defaultMiddleWedgeType
          );

          if (currentIndex === -1) {
            return true;
          }

          let newIndex = currentIndex;

          if (event.key === 'ArrowUp') {
            newIndex = Math.min(
              currentIndex + 1,
              this.state.availableWedgeTypes.length - 1
            );
          } else if (event.key === 'ArrowDown') {
            newIndex = Math.max(currentIndex - 1, 0);
          } else {
            return true;
          }

          if (newIndex !== currentIndex) {
            this.state.defaultMiddleWedgeType =
              this.state.availableWedgeTypes[newIndex].type;
            this.prevVisualizationRef = null; // Clear cache to force recalculation

            // Redraw to show the new preview
            this.renderPreview();
          }
          return true;
        }
      }
      return false;
    };

    // Use capture phase to ensure we handle the event before the viewer
    window.addEventListener('keydown', this._keyDownHandler, true);
  }

  deactivate(): void {
    this.viewer.setSelectionMode(Autodesk.Viewing.SelectionMode.REGULAR);
    this.clear();
    this.viewer.impl.canvas.style.cursor = 'default';

    // Remove camera change listener
    this.viewer.removeEventListener(
      Autodesk.Viewing.CAMERA_CHANGE_EVENT,
      this._onCameraChange
    );

    // Remove keyboard event listener
    if (this._keyDownHandler) {
      window.removeEventListener('keydown', this._keyDownHandler, true);
      this._keyDownHandler = null;
    }
  }

  private _onCameraChange = () => {
    // Update all screen-space vertices and labels when camera changes
    this.screenSpaceVertices.forEach((vertex) => vertex.update());
    this.screenSpaceLabels.forEach((label) => label.update());
  };

  clear(): void {
    this.clearPreviewShapes();
    this.clearStaticShapes();
    this.clearVertices();
    this.clearLabels();

    // Reset state
    this.pointsArray = [];
    this.cursorPoint = null;
    this.hoveredPoint = null;
    this.activePoints = [];
    this.activePointerInitPosition = null;
    this.activePointerCurrentPosition = null;
    this.dragType = null;
    this.dragMiddleIndex = null;
  }

  getCursor(): string {
    if (this.activePoints.length > 0) return MOVE_CURSOR;
    if (this.hoveredPoint) return MOVE_CURSOR;
    return CROSSHAIR_CURSOR;
  }

  handleSingleClick(event: MouseEvent, button: number): boolean {
    if (button !== 0) return false;

    const point = this.getWorldPositionFromMouseEvent(event);
    if (!point) return false;

    // Convert to API coordinates
    const converter = new ApiCoordinatesConverter(
      this.viewer,
      this.activeSheetData.viewport
    );
    const apiPoint = converter.convertToApiPoint(point);

    this.handleClick([apiPoint.x, apiPoint.y], point);
    return true;
  }

  handleMouseMove(event: MouseEvent): boolean {
    const point = this.getWorldPositionFromMouseEvent(event);
    if (!point) return false;

    // Handle dragging
    if (this.activePoints.length > 0) {
      this.activePointerCurrentPosition = point;
      this.updateDragging();
      return true;
    }

    // Update hover states
    this.updateHoveredPoint(event);

    // Handle preview
    if (this.pointsArray.length === 1 && !this.state.isDrawingComplete) {
      this.cursorPoint = point;

      // Convert to API coordinates for preview calculation
      const converter = new ApiCoordinatesConverter(
        this.viewer,
        this.activeSheetData.viewport
      );
      const apiPoint = converter.convertToApiPoint(point);

      this.renderPreview([apiPoint.x, apiPoint.y]);
      return true; // Consume event during active drawing
    }

    // For completed wedges, only consume event if hovering over interactive elements
    if (this.hoveredPoint) {
      return true;
    }

    // Not interacting - allow panning
    return false;
  }

  handleButtonDown(event: MouseEvent, button: number): boolean {
    if (button !== 0) return false;

    const point = this.getWorldPositionFromMouseEvent(event);
    if (!point) return false;

    this.activePointerInitPosition = point;
    this.activePointerCurrentPosition = point;

    if (this.hoveredPoint) {
      this.activePoints = [this.hoveredPoint];

      // Determine drag type based on the point's location
      const pointIndex = this.hoveredPoint.locations[0][1];
      if (pointIndex === 0) {
        this.dragType = 'start';
      } else if (pointIndex === this.pointsArray.length - 1) {
        this.dragType = 'end';
      } else {
        this.dragType = 'middle';
        this.dragMiddleIndex = pointIndex;
      }

      return true;
    }

    return false;
  }

  handleButtonUp(event: MouseEvent): boolean {
    const cursorPosition = this.getWorldPositionFromMouseEvent(event);
    if (!cursorPosition) return false;

    const cursorDistance =
      this.activePointerInitPosition?.distanceTo(cursorPosition) ?? 0;
    const moved = this.activePoints.length > 0 && cursorDistance > 0;

    if (moved) {
      this.applyDragging();
    }

    this.activePoints = [];
    this.activePointerInitPosition = null;
    this.activePointerCurrentPosition = null;
    this.dragType = null;
    this.dragMiddleIndex = null;

    return moved;
  }

  private getWorldPositionFromMouseEvent(
    event: MouseEvent
  ): THREE.Vector3 | null {
    const viewerElement = this.viewer.impl.canvas.parentElement;
    if (!viewerElement) return null;

    const viewerRect = viewerElement.getBoundingClientRect();
    const relativeX = event.clientX - viewerRect.left;
    const relativeY = event.clientY - viewerRect.top;

    return this.viewer.impl.intersectGround(relativeX, relativeY);
  }

  private handleClick(apiPoint: Point2, viewerPoint: THREE.Vector3): void {
    if (this.pointsArray.length === 0) {
      // First point

      this.pointsArray = [viewerPoint];
      this.state.points = [apiPoint];
      this.state.isDrawingComplete = false;
      this.clearPreviewShapes();
    } else if (this.pointsArray.length === 1) {
      // Second point - calculate final wedge
      const result = this.calculateFinalWedgePoints(apiPoint);

      this.state.points = result.points;
      this.state.diamonds = result.diamonds;
      this.state.kileTypes = result.kileTypes;
      this.state.isDrawingComplete = true;

      // Convert PDF coordinates back to viewer coordinates
      const converter = new ApiCoordinatesConverter(
        this.viewer,
        this.activeSheetData.viewport
      );
      this.pointsArray = result.points.map((point) => {
        const viewerPoint = converter.convertToAutodeskPoint({
          x: point[0],
          y: point[1],
        });
        return viewerPoint;
      });

      this.clearPreviewShapes();
      this.renderStaticShapes();
      this.renderVertices();
      this.renderLabels();

      // Call result callback
      this.callResultCallback();
    }
  }

  public handleKeyDown(event: KeyboardEvent): boolean {
    try {
      const currentIndex = this.state.availableWedgeTypes.findIndex(
        (item) => item.type === this.state.defaultMiddleWedgeType
      );

      if (currentIndex === -1) return false;

      let newIndex = currentIndex;

      if (event.key === 'ArrowUp') {
        newIndex = Math.min(
          currentIndex + 1,
          this.state.availableWedgeTypes.length - 1
        );
      } else if (event.key === 'ArrowDown') {
        newIndex = Math.max(currentIndex - 1, 0);
      } else {
        return false;
      }

      if (newIndex !== currentIndex) {
        this.state.defaultMiddleWedgeType =
          this.state.availableWedgeTypes[newIndex].type;
        this.prevVisualizationRef = null; // Clear cache to force recalculation
        return true;
      }

      return false;
    } catch (error) {
      console.error('[WedgeDrawingTool] Error in handleKeyDown:', error);
      return false;
    }
  }

  private renderPreview(intermediatePoint?: Point2): void {
    if (!intermediatePoint && this.cursorPoint) {
      const converter = new ApiCoordinatesConverter(
        this.viewer,
        this.activeSheetData.viewport
      );
      const apiPoint = converter.convertToApiPoint(this.cursorPoint);
      intermediatePoint = [apiPoint.x, apiPoint.y];
    }

    if (!intermediatePoint || this.state.points.length === 0) {
      return;
    }

    const previewResult = this.calculatePreview(intermediatePoint);

    if (previewResult) {
      this.renderPreviewShapes(previewResult);
    }
  }

  private calculatePreview(
    intermediatePoint: Point2
  ): WedgePreviewResult | null {
    if (
      this.state.isDrawingComplete ||
      !this.unitScale ||
      this.state.points.length === 0
    ) {
      return null;
    }

    // Check if we need to recalculate
    const shouldRecalculate = this.needsWedgeRecalculation(intermediatePoint);

    if (
      this.prevVisualizationRef &&
      !shouldRecalculate &&
      this.edgeDetectionProcessedRef
    ) {
      return this.prevVisualizationRef;
    }

    if (shouldRecalculate) {
      this.edgeDetectionProcessedRef = false;
    }

    const startPoint = this.state.points[0];

    // Calculate new wedge points
    const result = calculateWedgePoints(
      startPoint,
      intermediatePoint,
      this.unitScale,
      this.state.diamonds[0],
      this.state.defaultMiddleWedgeType
    );

    // Apply edge detection adjustments
    this.applyEdgeDetectionAdjustments(result);

    // Store the mouse position and cache the result
    result.mousePosition = intermediatePoint;
    this.prevVisualizationRef = result;

    return result;
  }

  private calculateFinalWedgePoints(endPoint: Point2): WedgePreviewResult {
    if (!this.unitScale) {
      return {
        points: [this.state.points[0], endPoint],
        diamonds: this.state.diamonds,
        kileTypes: this.state.kileTypes,
      };
    }

    const startPoint = this.state.points[0];

    // Use the preview visualization if available
    const result = this.prevVisualizationRef
      ? {
          ...this.prevVisualizationRef,
          points: [...this.prevVisualizationRef.points],
        }
      : calculateWedgePoints(
          startPoint,
          endPoint,
          this.unitScale,
          this.state.diamonds[0],
          this.state.defaultMiddleWedgeType
        );

    // Apply edge detection adjustments
    this.applyEdgeDetectionAdjustments(result);

    return result;
  }

  private needsWedgeRecalculation(currentPoint: Point2): boolean {
    if (
      !this.prevVisualizationRef ||
      !this.prevVisualizationRef.mousePosition
    ) {
      return true;
    }

    const prevPoint = this.prevVisualizationRef.mousePosition;
    return (
      Math.abs(prevPoint[0] - currentPoint[0]) > 1 ||
      Math.abs(prevPoint[1] - currentPoint[1]) > 1
    );
  }

  private applyEdgeDetectionAdjustments(result: WedgePreviewResult): void {
    if (!this.unitScale || !this.renderedSheetShapes) {
      return;
    }

    const segments: LineSegment2[] = [];
    for (let i = 0; i < result.points.length - 1; i++) {
      segments.push([result.points[i], result.points[i + 1]]);
    }

    const newDiamonds: Diamond[] = result.diamonds.map((d, diamondIndex) => {
      if (diamondIndex === 0) {
        return {
          triangle1: false,
          triangle2: true,
          triangle3: false,
          triangle4: true,
        };
      } else if (diamondIndex === result.diamonds.length - 1) {
        return {
          triangle1: true,
          triangle2: false,
          triangle3: true,
          triangle4: false,
        };
      } else {
        return {
          triangle1: true,
          triangle2: true,
          triangle3: true,
          triangle4: true,
        };
      }
    });

    segments.forEach((segment, index) => {
      const closestEdgeResult = findClosestParallelEdgeToSegment(
        segment,
        this.renderedSheetShapes!,
        0.1,
        this.unitScale!
      );

      if (closestEdgeResult?.isCloserThan100mm && index < newDiamonds.length) {
        if (index === 0) {
          if (closestEdgeResult.isOnLeftSide) {
            newDiamonds[index].triangle2 = false;
          } else {
            newDiamonds[index].triangle4 = false;
          }
        } else if (index === segments.length - 1) {
          if (closestEdgeResult.isOnLeftSide) {
            newDiamonds[index].triangle1 = false;
          } else {
            newDiamonds[index].triangle3 = false;
          }
        } else {
          if (closestEdgeResult.isOnLeftSide) {
            newDiamonds[index].triangle1 = false;
            newDiamonds[index].triangle2 = false;
          } else {
            newDiamonds[index].triangle3 = false;
            newDiamonds[index].triangle4 = false;
          }
        }
      }
    });

    result.diamonds = newDiamonds;
  }

  private updateHoveredPoint(event: MouseEvent): void {
    this.hoveredPoint = null;

    const cursorPoint = this.getWorldPositionFromMouseEvent(event);
    if (!cursorPoint) return;

    const hoveredPoint = this.pointsArray.find((point) => {
      const distance = distanceBetweenInPx(this.viewer, cursorPoint, point);
      return distance && distance <= 15;
    });

    if (hoveredPoint) {
      const locations = findPointLocations([this.pointsArray], hoveredPoint);
      this.hoveredPoint = {
        point: hoveredPoint,
        locations,
      };
    }
  }

  // eslint-disable-next-line complexity
  private updateDragging(): void {
    if (
      !this.activePointerCurrentPosition ||
      !this.activePointerInitPosition ||
      !this.dragType
    ) {
      return;
    }

    // Calculate movement vector
    const movementVector = new THREE.Vector3(
      this.activePointerCurrentPosition.x - this.activePointerInitPosition.x,
      this.activePointerCurrentPosition.y - this.activePointerInitPosition.y
    );

    // Get the initial position of the point when dragging started
    const initialPointPosition = this.activePoints[0].point;

    const draggedPoint = new THREE.Vector3(
      initialPointPosition.x + movementVector.x,
      initialPointPosition.y + movementVector.y
    );

    // Convert to API coordinates
    const converter = new ApiCoordinatesConverter(
      this.viewer,
      this.activeSheetData.viewport
    );
    const newPointPdf = converter.convertToApiPoint(draggedPoint);

    let result: any = null;

    if (this.dragType === 'start' || this.dragType === 'end') {
      // Endpoint dragging
      const recalculateWedgePoints = (startPoint: Point2, endPoint: Point2) => {
        if (!this.unitScale) return null;
        const wedgeResult = calculateWedgePoints(
          startPoint,
          endPoint,
          this.unitScale,
          this.state.diamonds[0],
          this.state.defaultMiddleWedgeType
        );

        // Convert to the expected WedgeCalculationResult format
        const segments: LineSegment2[] = [];
        for (let i = 0; i < wedgeResult.points.length - 1; i++) {
          segments.push([wedgeResult.points[i], wedgeResult.points[i + 1]]);
        }

        return {
          ...wedgeResult,
          segmentMidpoints: segments.map(
            (segment) =>
              [
                (segment[0][0] + segment[1][0]) / 2,
                (segment[0][1] + segment[1][1]) / 2,
              ] as [number, number]
          ),
          segments,
        };
      };

      if (this.dragType === 'start') {
        // Dragging the first point (start point)
        result = handleStartPointDrag(
          [newPointPdf.x, newPointPdf.y],
          this.state.points,
          recalculateWedgePoints
        );
      } else {
        // Dragging the last point (end point)
        result = handleEndPointDrag(
          [newPointPdf.x, newPointPdf.y],
          this.state.points,
          recalculateWedgePoints
        );
      }

      if (result) {
        // Update state with endpoint drag result
        this.state.points = result.wedgePoints;
        this.state.diamonds = result.newDiamonds;
        this.state.kileTypes = result.wedgeKileTypes;
      }
    } else if (this.dragType === 'middle' && this.dragMiddleIndex !== null) {
      // Intermediate point dragging
      result = handleMiddlePointDrag(
        [newPointPdf.x, newPointPdf.y],
        this.dragMiddleIndex,
        this.state.points,
        this.state.diamonds,
        this.state.kileTypes,
        this.unitScale!
      );

      if (result?.newPoints) {
        // Check if a collapse occurred (points array got shorter)
        const collapseOccurred =
          result.newPoints.length < this.state.points.length;

        // Update state with midpoint drag result
        this.state.points = result.newPoints;
        this.state.diamonds = result.newDiamonds;
        this.state.kileTypes = result.newKileTypes;

        // If a collapse occurred, we need to stop dragging since the dragged point no longer exists
        if (collapseOccurred) {
          // Apply edge detection adjustments after collapse
          const collapseResult: WedgePreviewResult = {
            points: this.state.points,
            diamonds: this.state.diamonds,
            kileTypes: this.state.kileTypes,
          };

          this.applyEdgeDetectionAdjustments(collapseResult);

          // Update state with edge-adjusted diamonds
          this.state.diamonds = collapseResult.diamonds;

          this.activePoints = [];
          this.dragType = null;
          this.dragMiddleIndex = null;
          this.hoveredPoint = null; // Clear hovered point as it might reference the old point

          // Update pointsArray to reflect the new state
          const converter = new ApiCoordinatesConverter(
            this.viewer,
            this.activeSheetData.viewport
          );
          this.pointsArray = this.state.points.map((point) => {
            const viewerPoint = converter.convertToAutodeskPoint({
              x: point[0],
              y: point[1],
            });
            return viewerPoint;
          });

          // Immediately re-render to update the UI
          this.renderStaticShapes();
          this.clearVertices();
          this.renderVertices();
          this.renderLabels();

          // Update results after collapse
          this.callResultCallback();
          return; // Exit early, don't continue with rendering
        }
      }
    }

    if (result) {
      // Apply edge detection adjustments to the result
      const dragResult: WedgePreviewResult = {
        points: this.state.points,
        diamonds: this.state.diamonds,
        kileTypes: this.state.kileTypes,
      };

      this.applyEdgeDetectionAdjustments(dragResult);

      // Update state with edge-adjusted diamonds
      this.state.diamonds = dragResult.diamonds;

      // Convert back to viewer coordinates
      this.pointsArray = this.state.points.map((point) => {
        const viewerPoint = converter.convertToAutodeskPoint({
          x: point[0],
          y: point[1],
        });
        return viewerPoint;
      });

      // Re-render
      this.renderStaticShapes();
      this.renderVertices();
      this.renderLabels();
    }
  }

  private applyDragging(): void {
    // The dragging is already applied in updateDragging
    // Just call the result callback
    this.callResultCallback();
  }

  private isSingleSided(diamond: Diamond): boolean {
    return (
      (!diamond.triangle1 && !diamond.triangle3) ||
      (!diamond.triangle2 && !diamond.triangle4)
    );
  }

  private renderPreviewShapes(previewResult: WedgePreviewResult): void {
    this.clearPreviewShapes();

    if (!this.edit2d?.defaultContext?.layer) {
      return;
    }

    const converter = new ApiCoordinatesConverter(
      this.viewer,
      this.activeSheetData.viewport
    );

    const shapesToAdd: Autodesk.Edit2D.Shape[] = [];

    // Render diamond previews for each segment
    for (let i = 0; i < previewResult.points.length - 1; i++) {
      const startPoint = previewResult.points[i];
      const endPoint = previewResult.points[i + 1];
      const diamond = previewResult.diamonds[i];

      if (!diamond) continue;

      const segment: LineSegment2 = [startPoint, endPoint];
      const diamondPlacement = this.isSingleSided(diamond)
        ? 'ONESIDE'
        : 'CENTER';

      const trianglePoints = createDiamondAroundSegment(
        segment,
        diamondPlacement
      );

      const diamondTriangles = [
        { points: trianglePoints[0], visible: diamond.triangle1 },
        { points: trianglePoints[1], visible: diamond.triangle2 },
        { points: trianglePoints[2], visible: diamond.triangle3 },
        { points: trianglePoints[3], visible: diamond.triangle4 },
      ];

      diamondTriangles.forEach(({ points, visible }) => {
        if (visible) {
          const convertedPoints = points.map((point) =>
            converter.convertToAutodeskPoint({ x: point[0], y: point[1] })
          );

          const vectorPoints = convertedPoints.map(
            (point) => new THREE.Vector3(point.x, point.y, 0)
          );

          const polygonShape = new Autodesk.Edit2D.Polygon(vectorPoints);
          polygonShape.style.lineColor = ChakraColor.orange400;
          polygonShape.style.fillColor = ChakraColor.orange200;
          polygonShape.style.lineWidth = 1;
          polygonShape.style.fillAlpha = 0.5;

          this.previewShapes.push(polygonShape);
          shapesToAdd.push(polygonShape);
        }
      });
    }

    // Batch add all shapes
    if (shapesToAdd.length > 0) {
      for (const shape of shapesToAdd) {
        this.edit2d.defaultContext.layer.addShape(shape);
      }
      this.edit2d.defaultContext.layer.update();
    }

    // Render preview labels
    this.renderPreviewLabels(previewResult);
  }

  private renderPreviewLabels(previewResult: WedgePreviewResult): void {
    this.clearLabels();

    if (!this.unitScale) return;

    // Create segments from points
    const segments: LineSegment2[] = [];
    for (let i = 0; i < previewResult.points.length - 1; i++) {
      segments.push([previewResult.points[i], previewResult.points[i + 1]]);
    }

    // Calculate product lengths for each segment
    const segmentProductLengths = calculateProductLengths(
      segments,
      previewResult.diamonds,
      this.unitScale
    );

    // Create labels for each segment
    segments.forEach((segment, segmentIndex) => {
      const segmentProductLength = segmentProductLengths[segmentIndex] || 0;
      const segmentWedgeLabel =
        segmentProductLength > 0 ? createWedgeLabel(segmentProductLength) : '';

      if (segmentWedgeLabel) {
        // Calculate rotation angle from segment direction, accounting for PDF rotation
        const pdfRotation = this.activeSheetData.viewport.rotation || 0;
        const rotation = calculateRotationFromSegment(segment, pdfRotation);

        // Pass the correct diamond for preview
        this.createSegmentLabels(
          segment,
          segmentWedgeLabel,
          rotation,
          segmentIndex,
          previewResult.diamonds[segmentIndex]
        );
      }
    });
  }

  private renderStaticShapes(): void {
    this.clearStaticShapes();

    if (!this.state.isDrawingComplete || !this.edit2d?.defaultContext?.layer) {
      return;
    }

    const converter = new ApiCoordinatesConverter(
      this.viewer,
      this.activeSheetData.viewport
    );

    const shapesToAdd: Autodesk.Edit2D.Shape[] = [];

    // Render static diamond shapes for completed wedge
    for (let i = 0; i < this.state.points.length - 1; i++) {
      const shapes = this.createDiamondShapes(i, converter);
      shapesToAdd.push(...shapes);
    }

    // Batch add all shapes
    this.addShapesToLayer(shapesToAdd);
  }

  private createDiamondShapes(
    segmentIndex: number,
    converter: ApiCoordinatesConverter
  ): Autodesk.Edit2D.Shape[] {
    const startPoint = this.state.points[segmentIndex];
    const endPoint = this.state.points[segmentIndex + 1];
    const diamond = this.state.diamonds[segmentIndex];

    if (!diamond) return [];

    const segment: LineSegment2 = [startPoint, endPoint];
    const diamondPlacement = this.isSingleSided(diamond) ? 'ONESIDE' : 'CENTER';

    const trianglePoints = createDiamondAroundSegment(
      segment,
      diamondPlacement
    );
    const shapes: Autodesk.Edit2D.Shape[] = [];

    const diamondTriangles = [
      { points: trianglePoints[0], visible: diamond.triangle1 },
      { points: trianglePoints[1], visible: diamond.triangle2 },
      { points: trianglePoints[2], visible: diamond.triangle3 },
      { points: trianglePoints[3], visible: diamond.triangle4 },
    ];

    diamondTriangles.forEach(({ points, visible }) => {
      if (visible) {
        const shape = this.createPolygonShape(points, converter);
        shapes.push(shape);
        this.staticShapes.push(shape);
      }
    });

    return shapes;
  }

  private createPolygonShape(
    points: Point2[],
    converter: ApiCoordinatesConverter
  ): Autodesk.Edit2D.Polygon {
    const convertedPoints = points.map((point) =>
      converter.convertToAutodeskPoint({ x: point[0], y: point[1] })
    );

    const vectorPoints = convertedPoints.map(
      (point) => new THREE.Vector3(point.x, point.y, 0)
    );

    const polygonShape = new Autodesk.Edit2D.Polygon(vectorPoints);
    polygonShape.style.lineColor = ChakraColor.green400;
    polygonShape.style.fillColor = ChakraColor.green200;
    polygonShape.style.lineWidth = 1;
    polygonShape.style.fillAlpha = 0.5;

    return polygonShape;
  }

  private addShapesToLayer(shapes: Autodesk.Edit2D.Shape[]): void {
    if (shapes.length > 0) {
      for (const shape of shapes) {
        this.edit2d?.defaultContext?.layer?.addShape(shape);
      }
      this.edit2d!.defaultContext!.layer!.update();
    }
  }

  private renderVertices(): void {
    this.clearVertices();

    if (!this.state.isDrawingComplete) return;

    this.pointsArray.forEach((point, index) => {
      const isFirstVertex = index === 0;
      const style = isFirstVertex ? FirstVertexStyle : VertexStyle;

      const vertex = new ScreenSpaceVertex(
        this.viewer,
        () => this.pointsArray[index],
        style,
        isFirstVertex
      );
      this.screenSpaceVertices.push(vertex);
    });
  }

  private renderLabels(): void {
    this.clearLabels();

    if (!this.state.isDrawingComplete || !this.unitScale) return;

    // Create segments from points
    const segments: LineSegment2[] = [];
    for (let i = 0; i < this.state.points.length - 1; i++) {
      segments.push([this.state.points[i], this.state.points[i + 1]]);
    }

    // Calculate product lengths for each segment
    const segmentProductLengths = calculateProductLengths(
      segments,
      this.state.diamonds,
      this.unitScale
    );

    // Create labels for each segment
    segments.forEach((segment, segmentIndex) => {
      const segmentProductLength = segmentProductLengths[segmentIndex] || 0;
      const segmentWedgeLabel =
        segmentProductLength > 0 ? createWedgeLabel(segmentProductLength) : '';

      if (segmentWedgeLabel) {
        // Calculate rotation angle from segment direction, accounting for PDF rotation
        const pdfRotation = this.activeSheetData.viewport.rotation || 0;
        const rotation = calculateRotationFromSegment(segment, pdfRotation);

        // Create labels for left and right sides of the segment
        this.createSegmentLabels(
          segment,
          segmentWedgeLabel,
          rotation,
          segmentIndex
        );
      }
    });
  }

  // eslint-disable-next-line complexity
  private createSegmentLabels(
    segment: LineSegment2,
    label: string,
    rotation: number,
    segmentIndex: number,
    diamondOverride?: Diamond
  ): void {
    // Convert segment to viewer coordinates for positioning
    const converter = new ApiCoordinatesConverter(
      this.viewer,
      this.activeSheetData.viewport
    );

    const startPoint = converter.convertToAutodeskPoint({
      x: segment[0][0],
      y: segment[0][1],
    });
    const endPoint = converter.convertToAutodeskPoint({
      x: segment[1][0],
      y: segment[1][1],
    });

    // Calculate midpoint of the segment
    const midX = (startPoint.x + endPoint.x) / 2;
    const midY = (startPoint.y + endPoint.y) / 2;
    const midPoint = new THREE.Vector3(midX, midY, 0);

    // Use the override diamond if provided (for preview), otherwise use state
    const diamond = diamondOverride || this.state.diamonds[segmentIndex];
    const diamondPlacement = this.isSingleSided(diamond) ? 'ONESIDE' : 'CENTER';

    const trianglePoints = createDiamondAroundSegment(
      segment,
      diamondPlacement
    );

    // Group triangles by side (left/right)
    const leftTriangles = [trianglePoints[0], trianglePoints[2]]; // triangle1, triangle3
    const rightTriangles = [trianglePoints[1], trianglePoints[3]]; // triangle2, triangle4

    // Create label for left side if there are visible triangles
    if (diamond.triangle1 || diamond.triangle3) {
      const leftCenter = this.calculateTriangleGroupCenter(
        leftTriangles,
        converter
      );
      if (leftCenter) {
        const leftLabel = new ScreenSpaceLabel(
          this.viewer,
          () => leftCenter,
          () => label,
          () => rotation
        );
        this.screenSpaceLabels.push(leftLabel);
      }
    }

    // Create label for right side if there are visible triangles
    if (diamond.triangle2 || diamond.triangle4) {
      const rightCenter = this.calculateTriangleGroupCenter(
        rightTriangles,
        converter
      );
      if (rightCenter) {
        const rightLabel = new ScreenSpaceLabel(
          this.viewer,
          () => rightCenter,
          () => label,
          () => rotation
        );
        this.screenSpaceLabels.push(rightLabel);
      }
    }
  }

  private calculateTriangleGroupCenter(
    triangles: Point2[][],
    converter: ApiCoordinatesConverter
  ): THREE.Vector3 | null {
    const allPoints: THREE.Vector3[] = [];

    triangles.forEach((triangle) => {
      triangle.forEach((point) => {
        const viewerPoint = converter.convertToAutodeskPoint({
          x: point[0],
          y: point[1],
        });
        allPoints.push(new THREE.Vector3(viewerPoint.x, viewerPoint.y, 0));
      });
    });

    if (allPoints.length === 0) return null;

    // Calculate center of all points
    const centerX =
      allPoints.reduce((sum, point) => sum + point.x, 0) / allPoints.length;
    const centerY =
      allPoints.reduce((sum, point) => sum + point.y, 0) / allPoints.length;

    return new THREE.Vector3(centerX, centerY, 0);
  }

  private clearPreviewShapes(): void {
    if (this.edit2d?.defaultContext?.layer && this.previewShapes.length > 0) {
      for (const shape of this.previewShapes) {
        this.edit2d.defaultContext.layer.removeShape(shape);
      }
      this.edit2d.defaultContext.layer.update();
    }
    this.previewShapes = [];
  }

  private clearStaticShapes(): void {
    if (this.edit2d?.defaultContext?.layer && this.staticShapes.length > 0) {
      for (const shape of this.staticShapes) {
        this.edit2d.defaultContext.layer.removeShape(shape);
      }
      this.edit2d.defaultContext.layer.update();
    }
    this.staticShapes = [];
  }

  private clearVertices(): void {
    this.screenSpaceVertices.forEach((vertex) => vertex.remove());
    this.screenSpaceVertices = [];
  }

  private clearLabels(): void {
    this.screenSpaceLabels.forEach((label) => label.remove());
    this.screenSpaceLabels = [];
  }

  private callResultCallback(): void {
    if (this._onResult) {
      // Convert state.points (API coordinates) to viewer coordinates for the callback
      const converter = new ApiCoordinatesConverter(
        this.viewer,
        this.activeSheetData.viewport
      );
      const viewerPoints = this.state.points.map((point) => {
        const viewerPoint = converter.convertToAutodeskPoint({
          x: point[0],
          y: point[1],
        });
        return new THREE.Vector3(viewerPoint.x, viewerPoint.y, 0);
      });

      // Call the callback with the viewer coordinates format expected by updatePointsCallback
      this._onResult([viewerPoints]);
    }
  }

  // Public methods for external access
  public getResults(): any {
    // Use state.points which contains the correct API coordinates after any collapses
    const points = this.state.points;

    // Ensure we have at least 2 points for a valid wedge
    const isValid = this.state.isDrawingComplete && points.length >= 2;

    // Calculate total wedge product length as sum of (segment length * number of wedges on that segment)
    let totalWedgeProductLength = 0;
    if (isValid && this.unitScale) {
      const segments: LineSegment2[] = [];
      for (let i = 0; i < points.length - 1; i++) {
        segments.push([points[i], points[i + 1]]);
      }

      totalWedgeProductLength = segments.reduce((sum, segment, index) => {
        const diamond = this.state.diamonds[index];
        if (!diamond || !this.unitScale) return sum;

        const dx = segment[1][0] - segment[0][0];
        const dy = segment[1][1] - segment[0][1];
        const lengthMeters =
          (Math.sqrt(dx * dx + dy * dy) * this.unitScale) /
          (this.isSingleSided(diamond) ? 1 : 2);

        const wedgeCount =
          (diamond.triangle1 ? 1 : 0) +
          (diamond.triangle2 ? 1 : 0) +
          (diamond.triangle3 ? 1 : 0) +
          (diamond.triangle4 ? 1 : 0);

        const contribution = lengthMeters * wedgeCount;
        return sum + contribution;
      }, 0);

      // Round to 2 decimals to avoid floating point noise
      totalWedgeProductLength = Math.round(totalWedgeProductLength * 100) / 100;
    }

    return {
      points,
      valid: isValid,
      wedgeData: {
        diamonds: this.state.diamonds,
        kileTypes: this.state.kileTypes,
        totalWedgeProductLength,
      },
    };
  }

  public setInitialState(points: Point2[]): void {
    // Convert API coordinates to viewer coordinates
    const converter = new ApiCoordinatesConverter(
      this.viewer,
      this.activeSheetData.viewport
    );

    this.pointsArray = points.map((point) => {
      const viewerPoint = converter.convertToAutodeskPoint({
        x: point[0],
        y: point[1],
      });
      return new THREE.Vector3(viewerPoint.x, viewerPoint.y, 0);
    });

    // Update state with points
    this.state.points = points;
    this.state.isDrawingComplete = true;

    // Use the tool's existing calculation logic to determine diamonds and kileTypes
    // This will apply edge detection and calculate proper wedge types
    this.calculateWedgeDataFromPoints(points);

    // Render everything
    this.renderStaticShapes();
    this.renderVertices();
    this.renderLabels();
  }

  private calculateWedgeDataFromPoints(points: Point2[]): void {
    if (!this.unitScale || points.length < 2) {
      // Fallback to default values
      this.state.diamonds = [
        {
          triangle1: true,
          triangle2: true,
          triangle3: true,
          triangle4: true,
        },
      ];
      this.state.kileTypes = [KileTypeSKU.RW257379];
      return;
    }

    // Create segments from points
    const segments: LineSegment2[] = [];
    for (let i = 0; i < points.length - 1; i++) {
      segments.push([points[i], points[i + 1]]);
    }

    // Calculate base diamonds for each segment
    const baseDiamonds = segments.map((_, index) => {
      if (index === 0) {
        // First segment: triangle1 and triangle3 should be false (only second triangles visible)
        return {
          triangle1: false,
          triangle2: true,
          triangle3: false,
          triangle4: true,
        };
      } else if (index === segments.length - 1) {
        // Last segment: triangle2 and triangle4 should be false (only first triangles visible)
        return {
          triangle1: true,
          triangle2: false,
          triangle3: true,
          triangle4: false,
        };
      } else {
        // Middle segments: all triangles visible
        return {
          triangle1: true,
          triangle2: true,
          triangle3: true,
          triangle4: true,
        };
      }
    });

    // Adjust diamonds based on edge detection if we have the required data
    const diamonds = this.renderedSheetShapes
      ? this.adjustDiamondsForEdgeProximity(segments, baseDiamonds)
      : baseDiamonds;

    // Calculate kile types for each segment
    const kileTypes = segments.map((segment, index) => {
      const diamond = diamonds[index];

      // Calculate segment length in meters
      const segmentLengthPdf = Math.sqrt(
        Math.pow(segment[1][0] - segment[0][0], 2) +
          Math.pow(segment[1][1] - segment[0][1], 2)
      );
      const segmentLengthMeters = segmentLengthPdf * this.unitScale!;

      // Round to 2 decimal places to avoid floating point issues
      const roundedLength = Math.round(segmentLengthMeters * 100) / 100;

      // Get kile type using the wedge calculation utility
      const kileType = this.selectWedgeType(diamond, roundedLength);

      // Default to a common type if no valid type returned
      return kileType || KileTypeSKU.RW257379;
    });

    this.state.diamonds = diamonds;
    this.state.kileTypes = kileTypes;
  }

  private adjustDiamondsForEdgeProximity(
    segments: LineSegment2[],
    baseDiamonds: Diamond[]
  ): Diamond[] {
    if (!this.unitScale || !this.renderedSheetShapes) {
      return baseDiamonds;
    }

    return baseDiamonds.map((diamond, index) => {
      const edgeResult = findClosestParallelEdgeToSegment(
        segments[index],
        this.renderedSheetShapes!,
        0.1,
        this.unitScale!
      );

      // If no closest edge found, return the base diamond
      if (!edgeResult || !edgeResult.isCloserThan100mm) return diamond;

      // Create a copy of the diamond to modify
      const adjustedDiamond = { ...diamond };

      // Only modify diamonds for edges closer than 1m
      if (index === 0) {
        // First segment: only modify triangle2 and triangle4 based on edge position
        if (edgeResult.isOnLeftSide) {
          adjustedDiamond.triangle2 = false;
        } else {
          adjustedDiamond.triangle4 = false;
        }
      } else if (index === segments.length - 1) {
        // Last segment: only modify triangle1 and triangle3 based on edge position
        if (edgeResult.isOnLeftSide) {
          adjustedDiamond.triangle1 = false;
        } else {
          adjustedDiamond.triangle3 = false;
        }
      } else {
        // Middle segments: modify all triangles based on edge position
        if (edgeResult.isOnLeftSide) {
          adjustedDiamond.triangle1 = false;
          adjustedDiamond.triangle2 = false;
        } else {
          adjustedDiamond.triangle3 = false;
          adjustedDiamond.triangle4 = false;
        }
      }

      return adjustedDiamond;
    });
  }

  private selectWedgeType(
    diamond: Diamond,
    segmentLengthInMeters: number
  ): KileTypeSKU {
    // Check if we have single-sided wedges
    const isSingleSided =
      (!diamond.triangle1 && !diamond.triangle3) ||
      (!diamond.triangle2 && !diamond.triangle4);

    // If we have only one side wedges, use the segment length as is
    // If we have wedges on both sides, the product length is half the segment length
    const effectiveLength = isSingleSided
      ? segmentLengthInMeters
      : segmentLengthInMeters / 2;

    // Use the kile selector to get the appropriate type based on the diamond and length
    // This is a simplified version - in a real implementation, you'd import the actual kileSelector function
    const selectedType = this.getKileTypeFromLength(diamond, effectiveLength);
    return selectedType;
  }

  private getKileTypeFromLength(
    diamond: Diamond,
    effectiveLength: number
  ): KileTypeSKU {
    // This is a simplified implementation - in the real code, you'd use the actual kileSelector logic
    // For now, return a default type based on length ranges
    const lengthRanges = [
      { max: 1.2, type: KileTypeSKU.RW80110 },
      { max: 1.8, type: KileTypeSKU.RW80111 },
      { max: 2.4, type: KileTypeSKU.RW80112 },
      { max: 3.0, type: KileTypeSKU.RW257379 },
      { max: 3.6, type: KileTypeSKU.RW257426 },
      { max: 4.2, type: KileTypeSKU.RW257435 },
      { max: 4.8, type: KileTypeSKU.RW257437 },
      { max: 5.4, type: KileTypeSKU.RW257439 },
      { max: 6.0, type: KileTypeSKU.RW257440 },
      { max: 6.6, type: KileTypeSKU.RW257441 },
    ];

    const range = lengthRanges.find((r) => effectiveLength <= r.max);
    return range?.type || KileTypeSKU.RW257442;
  }

  public updateContext(
    calibration: SheetScaleType | null,
    renderedSheetShapes: Record<string, SheetShapeDeepFragment[]> | null
  ): void {
    this.calibration = calibration;
    this.renderedSheetShapes = renderedSheetShapes;

    // Add defensive check for calibration
    if (calibration) {
      try {
        this.unitScale = getUnitScaleFromCalibration(calibration);
      } catch (error) {
        console.warn('Failed to get unit scale from calibration:', error);
        this.unitScale = null;
      }
    } else {
      this.unitScale = null;
    }

    // Clear cache to force recalculation with new context
    this.prevVisualizationRef = null;
    this.edgeDetectionProcessedRef = false;
  }

  getName(): string {
    return 'Wedge';
  }
}
